// Sign in. One field, no password.
//
// The response is deliberately identical whether the address is known,
// unknown, pending or blocked. A different message for each would let anyone
// with the URL find out who has an account here.
import { useState } from 'react';

declare const __BUILD_STAMP__: string;
const BUILD_STAMP = typeof __BUILD_STAMP__ === 'string' ? __BUILD_STAMP__ : 'dev';

export function Login({ onRequest }: { onRequest: (email: string) => Promise<string> }) {
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.includes('@') || busy) return;
    setBusy(true);
    setMessage(await onRequest(email));
    setBusy(false);
  };

  return (
    <main className="login">
      <h1>Get Me Home</h1>
      <p className="blurb">
        Your next five quickest routes to where you are going, from where you are standing.
      </p>

      {message ? (
        <div className="banner soft">
          <p style={{ margin: 0 }}>{message}</p>
          <button className="link" onClick={() => setMessage(null)}>
            Use a different address
          </button>
        </div>
      ) : (
        <form onSubmit={submit}>
          <label htmlFor="email">Email address</label>
          <input
            id="email"
            type="email"
            inputMode="email"
            autoComplete="email"
            autoCapitalize="off"
            spellCheck={false}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            required
          />
          <button type="submit" className="primary" disabled={busy}>
            {busy ? 'Sending…' : 'Send me a sign-in link'}
          </button>
          <p className="hint">
            No password. New addresses need approving by Luke first, so the first request may
            take a little while.
          </p>
          {/* Same purpose as the footer stamp: makes a stale device obvious. */}
          <p className="hint stamp">build {BUILD_STAMP}</p>
        </form>
      )}
    </main>
  );
}

/** The header avatar. Initials, because a photo would be more work than it is worth. */
export function Avatar({
  initials,
  onClick,
  title,
}: {
  initials: string;
  onClick?: () => void;
  title?: string;
}) {
  return (
    <button className="avatar" onClick={onClick} title={title} aria-label={title ?? 'Account'}>
      {initials}
    </button>
  );
}
